import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  useTheme,
  Stack,
  Button,
  TextField,
  Backdrop,
  Popper,
  Paper,
  Tooltip,
  LinearProgress,
  Theme,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import RemoveIcon from "@mui/icons-material/Remove";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AddIcon from "@mui/icons-material/Add";
import { DateCalendar, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import useAfasData from "../hooks/useAfasData";
import { LanguageContext } from "../context/LanguageContext";

dayjs.extend(isBetween);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);

interface EmployeeBezetting {
  employee: string;
  employee_name: string;
  work_remote_days: string[];
  free_schedule: {
    date: string;
    hours: number;
  }[];
  sick: {
    date: string;
    hours: number;
    type: string[];
  }[];
  leave: {
    date: string;
    hours: number;
    type: string[];
  }[];
  parental: {
    date: string;
    hours: number;
  }[];
}

// Custom styled components
const StyledTableCell = styled(TableCell)(({ theme }: { theme: Theme }) => ({
  padding: "0px",
  borderRight: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderRight: "none",
  },
  whiteSpace: "nowrap",
  position: "relative",
}));

const NameCell = styled(StyledTableCell)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.common.black,
  position: "sticky",
  left: 0,
  zIndex: 2,
  paddingLeft: ".5rem",
  minWidth: "230px",
  borderLeft: "unset",
}));

const HeaderCell = styled(StyledTableCell)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.dark.main,
  color: theme.palette.common.white,
  fontFamily: "Univers Cn",
  fontWeight: "bold",
  textAlign: "center",
  padding: "1rem 1rem",
  textTransform: "uppercase",
}));

// Type for the cell status
type CellStatus = "remote" | "sick" | "leave" | "schedule" | "none" | "parental";

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean;
  isHovered: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApplyStylesFunction = (mode: string, styles: any) => any;

// Utility type to check if a theme has our custom method
type ThemeWithApplyStyles = Theme & {
  applyStyles: ApplyStylesFunction;
};

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop: string) => prop !== "isSelected" && prop !== "isHovered",
})<CustomPickerDayProps>(
  ({
    theme,
    isSelected,
    isHovered,
    day,
  }: {
    theme: Theme;
    isSelected: boolean;
    isHovered: boolean;
    day: Dayjs | null;
  }) => {
    // Use a type guard to check if the theme has our custom method
    const hasApplyStyles = (t: Theme): t is ThemeWithApplyStyles =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      "applyStyles" in t && typeof (t as any).applyStyles === "function";

    return {
      borderRadius: 0,
      ...(isSelected && {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&:hover, &:focus": {
          backgroundColor: theme.palette.primary.main,
        },
      }),
      ...(isHovered && {
        backgroundColor: theme.palette.primary.light,
        "&:hover, &:focus": {
          backgroundColor: theme.palette.primary.light,
        },
        // Use the type guard to safely call applyStyles
        ...(hasApplyStyles(theme)
          ? theme.applyStyles("dark", {
              backgroundColor: theme.palette.primary.dark,
              "&:hover, &:focus": {
                backgroundColor: theme.palette.primary.dark,
              },
            })
          : {}),
      }),
      ...(day?.day() === 1 && {
        borderTopLeftRadius: "50%",
        borderBottomLeftRadius: "50%",
      }),
      ...(day?.day() === 0 && {
        borderTopRightRadius: "50%",
        borderBottomRightRadius: "50%",
      }),
    };
  }
) as React.ComponentType<CustomPickerDayProps>;

const isInSameWeek = (dayA: Dayjs, dayB: Dayjs | null | undefined) => {
  if (dayB == null) {
    return false;
  }

  return dayA.isSame(dayB, "week");
};

function Day(
  props: PickersDayProps<Dayjs> & {
    selectedDay?: Dayjs | null;
    hoveredDay?: Dayjs | null;
  }
) {
  const { day, selectedDay, hoveredDay, ...other } = props;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ px: 2.5 }}
      disableMargin
      selected={false}
      isSelected={isInSameWeek(day, selectedDay)}
      isHovered={isInSameWeek(day, hoveredDay)}
    />
  );
}

const PopoverBackdrop = styled(Backdrop)({
  backgroundColor: "transparent",
});

const Bezetting: React.FC = () => {
  const { strings } = useContext(LanguageContext);
  const weeksToShow = 3;
  const [data, setData] = useState<EmployeeBezetting[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const theme: any = useTheme();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs().startOf("week"));
  const [hoveredDay, setHoveredDay] = useState<Dayjs | null>(null);

  const cachedDataRef = useRef<Record<string, EmployeeBezetting[]>>({});
  const {
    data: bezettingData,
    isLoading: bezettingIsLoading,
    refetch: bezettingRefetch,
  } = useAfasData(`/occupancy/overview/${weeksToShow}/${selectedDate.format("YYYY-MM-DD")}`);

  useEffect(() => {
    const dateKey = selectedDate.format("YYYY-MM-DD");
    if (cachedDataRef.current[dateKey]) {
      setData(cachedDataRef.current[dateKey]);
      return;
    }
    bezettingRefetch({ throwOnError: false, cancelRefetch: false });
  }, [selectedDate, bezettingRefetch]);

  useEffect(() => {
    if (!bezettingIsLoading && bezettingData) {
      const dateKey = selectedDate.format("YYYY-MM-DD");
      setData(
        bezettingData.sort((a: EmployeeBezetting, b: EmployeeBezetting) =>
          a.employee_name.localeCompare(b.employee_name)
        )
      );
      cachedDataRef.current[dateKey] = bezettingData;
    }
  }, [bezettingData, bezettingIsLoading, selectedDate]);

  const generateWeekDates = (startDate: Dayjs): Dayjs[] => {
    const dates: Dayjs[] = [];
    let currentDate = startDate.startOf("week").add(0, "day");
    for (let i = 0; i < weeksToShow * 7; i++) {
      if (currentDate.day() !== 0 && currentDate.day() !== 6) {
        dates.push(currentDate.clone());
      }
      currentDate = currentDate.add(1, "day");
    }
    return dates;
  };

  const getWeekStartDate = (date: Dayjs): Dayjs => {
    return date.startOf("week").add(1, "day");
  };

  const [weekDates, setWeekDates] = useState<Dayjs[]>(generateWeekDates(getWeekStartDate(selectedDate)));

  useEffect(() => {
    setWeekDates(generateWeekDates(getWeekStartDate(selectedDate)));
  }, [selectedDate]);

  const formatDateHeader = (date: Dayjs): string => {
    return `${date.format("ddd").replace(".", "")} ${date.format("DD")}`;
  };

  const handleNextWeek = () => {
    setSelectedDate(selectedDate.add(1, "week"));
  };

  const handlePreviousWeek = () => {
    setSelectedDate(selectedDate.subtract(1, "week"));
  };

  interface CombinedStatus {
    type: CellStatus;
    hours: number;
    types?: string[];
  }

  // Update the getCellStatus function to return combined statuses
  const getCombinedCellStatus = (employee: EmployeeBezetting, date: Dayjs): CombinedStatus[] => {
    const dateStr = date.format("YYYY-MM-DD");
    const statuses: CombinedStatus[] = [];

    // Check all possible statuses and add them with their hours
    if (employee.sick.some((s) => s.date === dateStr)) {
      const sickEntry = employee.sick.find((s) => s.date === dateStr);
      if (sickEntry) {
        statuses.push({
          type: "sick",
          hours: sickEntry.hours,
          types: sickEntry.type,
        });
      }
    }

    if (employee.leave.some((l) => l.date === dateStr)) {
      const leaveEntry = employee.leave.find((l) => l.date === dateStr);
      if (leaveEntry) {
        statuses.push({
          type: "leave",
          hours: leaveEntry.hours,
          types: leaveEntry.type,
        });
      }
    }

    if (employee.parental.some((p) => p.date === dateStr)) {
      const parentalEntry = employee.parental.find((p) => p.date === dateStr);
      if (parentalEntry) {
        statuses.push({
          type: "parental",
          hours: parentalEntry.hours,
          types: [strings.occupancy.parental],
        });
      }
    }

    if (employee.work_remote_days.includes(dateStr)) {
      // For remote work, we need to calculate remaining hours
      const totalOtherHours = statuses.reduce((total, status) => total + status.hours, 0);
      const remoteHours = Math.max(0, 8 - totalOtherHours);
      if (remoteHours > 0) {
        statuses.push({ type: "remote", hours: remoteHours });
      }
    }

    if (employee.free_schedule.some((fs) => fs.date === dateStr)) {
      const scheduleEntry = employee.free_schedule.find((fs) => fs.date === dateStr);
      if (scheduleEntry) {
        statuses.push({
          type: "schedule",
          hours: scheduleEntry.hours,
          types: [strings.occupancy.schedulefree],
        });
      }
    }

    // If no statuses or total hours < 8, add "none" for remaining hours
    const totalHours = statuses.reduce((total, status) => total + status.hours, 0);
    if (totalHours < 8) {
      statuses.push({ type: "none", hours: 8 - totalHours });
    }

    // Sort by hours descending for consistent display
    return statuses.sort((a, b) => b.hours - a.hours);
  };

  const renderCombinedCellContent = (employee: EmployeeBezetting, date: Dayjs) => {
    const statuses = getCombinedCellStatus(employee, date);

    // If we only have one status that is "none", return null (empty cell)
    if (statuses.length === 1 && statuses[0].type === "none") {
      return null;
    }

    return (
      <>
        <Box sx={{ display: "flex", width: "100%", height: "100%", position: "relative" }}>
          {statuses.map((status, index) => {
            if (status.type === "none") return null; // Skip rendering empty space
            let backgroundColor;
            switch (status.type) {
              case "remote":
                return null;
                break;
              case "sick":
                backgroundColor = theme.palette.primary.main;
                break;
              case "leave":
                backgroundColor = theme.palette.green.main;
                break;
              case "parental":
                backgroundColor = theme.palette.warning.light;
                break;
              case "schedule":
                backgroundColor = theme.palette.grey[400];
                break;
              default:
                return null;
            }

            // Calculate width percentage based on hours
            const widthPercentage = (status.hours / 8) * 100;

            return status.types && status.types.length > 0 ? (
              <Tooltip
                key={index}
                arrow
                placement="top"
                title={status.types.join(", ").replace("Sick", strings.occupancy.sick)}
              >
                <Box
                  sx={{
                    backgroundColor,
                    width: `${widthPercentage}%`,
                    height: "100%",
                  }}
                />
              </Tooltip>
            ) : (
              <Box
                key={index}
                sx={{
                  backgroundColor,
                  width: `${widthPercentage}%`,
                  height: "100%",
                }}
              />
            );
          })}
        </Box>
        {/* Add home icon for remote work if applicable */}
        {employee.work_remote_days.includes(date.format("YYYY-MM-DD")) && (
          <Box
            sx={{
              zIndex: 1,
              position: "absolute",
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <HomeOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.secondary, opacity: 0.5 }} />
          </Box>
        )}
      </>
    );
  };

  const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);
  const handleClick = (event: React.SyntheticEvent) => {
    setAnchorEl(event.currentTarget as HTMLInputElement | null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <Box mt={6} mb={6}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Stack direction={"row"} spacing={2} useFlexGap sx={{ ml: "auto", alignItems: "center" }}>
          <Button
            size="small"
            sx={{ px: 1, minWidth: "auto" }}
            color="inherit"
            variant="outlined"
            onClick={handlePreviousWeek}
          >
            <RemoveIcon />
          </Button>

          <TextField
            id="date-week"
            variant="outlined"
            size="small"
            sx={(theme: Theme) => ({
              "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.dark.main },
              "& .MuiOutlinedInput-root": { borderRadius: 0 },
              "& .MuiOutlinedInput-input": { borderRadius: 0, paddingTop: "9.5px", paddingBottom: "9.5px" },
            })}
            value={`Week ${selectedDate.week()}, ${selectedDate.year()}`}
            onClick={handleClick}
          />
          <PopoverBackdrop open={open} onClick={handleClose} />
          <Popper id={id} open={open} anchorEl={anchorEl} autoFocus={false} sx={{ zIndex: 4 }}>
            <Paper sx={{ zIndex: 4 }}>
              <DateCalendar
                value={selectedDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedDate(newValue.startOf("week"));
                  }
                  handleClose();
                }}
                showDaysOutsideCurrentMonth
                displayWeekNumber
                slots={{ day: Day }}
                slotProps={{
                  day: (ownerState) => ({
                    selectedDay: selectedDate,
                    hoveredDay,
                    onPointerEnter: () => setHoveredDay(ownerState.day),
                    onPointerLeave: () => setHoveredDay(null),
                  }),
                }}
              />
            </Paper>
          </Popper>
          <Button
            size="small"
            sx={{ px: 1, minWidth: "auto" }}
            color="inherit"
            variant="outlined"
            onClick={handleNextWeek}
          >
            <AddIcon />
          </Button>
        </Stack>
      </Box>

      <Box sx={{ mb: 1 }}>
        <Stack
          direction={"row"}
          spacing={4}
          useFlexGap
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Stack direction="row" alignItems="center">
            <Box mr={1} sx={{ height: "24px" }}>
              <HomeOutlinedIcon
                width={"16px"}
                height={"16px"}
                sx={(theme: Theme) => ({
                  color: theme.palette.grey[400],
                })}
              />
            </Box>
            <Typography variant="body1">{strings.occupancy.remote}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Box
              sx={(theme: Theme) => ({
                width: 16,
                height: 16,
                bgcolor: theme.palette.grey[400],
                mr: 1,
              })}
            />
            <Typography variant="body1">{strings.occupancy.schedulefree}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Box
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              sx={(theme: any) => ({
                width: 16,
                height: 16,
                bgcolor: theme.palette.green.main,
                mr: 1,
              })}
            />
            <Typography variant="body1">{strings.occupancy.leave}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Box
              sx={(theme: Theme) => ({
                width: 16,
                height: 16,
                bgcolor: theme.palette.primary.main,
                mr: 1,
              })}
            />
            <Typography variant="body1">{strings.occupancy.sick}</Typography>
          </Stack>
          <Tooltip arrow placement="top" title={strings.occupancy.parentaltypes}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={(theme: Theme) => ({
                  width: 16,
                  height: 16,
                  bgcolor: theme.palette.warning.light,
                  mr: 1,
                })}
              />
              <Typography variant="body1">{strings.occupancy.parental}</Typography>
            </Box>
          </Tooltip>
        </Stack>
      </Box>

      <Box width={"100%"} height={10}>
        {bezettingIsLoading && <LinearProgress />}
      </Box>
      <TableContainer sx={{ overflow: "auto" }}>
        <Table sx={{ tableLayout: "fixed" }} size="small">
          <TableHead>
            <TableRow>
              <HeaderCell sx={{ textAlign: "left" }} colSpan={3}>
                {strings.occupancy.title}
              </HeaderCell>
              {/* Month headers row */}
              {(() => {
                const monthCells = [];
                let currentMonth: string | null = null;
                let colSpan = 0;
                weekDates.forEach((date, idx) => {
                  const month = date.format("MMMM");
                  if (currentMonth === null || month !== currentMonth) {
                    if (currentMonth !== null) {
                      monthCells.push(
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            fontFamily: "Univers Cn",
                            padding: "1rem 1rem",
                            borderLeft: "1px solid",
                          }}
                          key={`month-${idx - colSpan}`}
                          colSpan={colSpan}
                        >
                          {currentMonth}
                        </TableCell>
                      );
                    }
                    currentMonth = month;
                    colSpan = 1;
                  } else {
                    colSpan++;
                  }
                });
                if (currentMonth !== null) {
                  monthCells.push(
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontFamily: "Univers Cn",
                        padding: "1rem 1rem",
                        borderLeft: "1px solid",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      key={`month-${weekDates.length - colSpan}`}
                      colSpan={colSpan}
                    >
                      {currentMonth}
                    </TableCell>
                  );
                }
                return monthCells;
              })()}
            </TableRow>
            <TableRow>
              <HeaderCell sx={{ textAlign: "left" }} colSpan={3}>
                {strings.occupancy.name}
              </HeaderCell>
              {weekDates.map((date, index) => (
                <HeaderCell
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  sx={(theme: any) => ({
                    backgroundColor: dayjs().isSame(date, "day")
                      ? theme.palette.green.main
                      : theme.palette.dark.main,
                  })}
                  key={index}
                >
                  {formatDateHeader(date)}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((employee, index) => (
              <TableRow
                key={index}
                sx={{
                  border: "unset",
                  "&:nth-of-type(even)": { backgroundColor: theme.palette.action.hover },
                  "&:hover": { backgroundColor: theme.palette.grey[300] },
                }}
              >
                <NameCell colSpan={3}>{employee.employee_name}</NameCell>
                {weekDates.map((date, dateIndex) => (
                  <StyledTableCell key={dateIndex} align="center" sx={{ height: 36 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      {renderCombinedCellContent(employee, date)}
                    </Box>
                  </StyledTableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Bezetting;
